import { lightTheme } from "./theme.mjs";
import { style } from "purse-styles";
import { memoize } from "@repo/utils/objectUtils";
import { match } from "@repo/utils/utils";

//#region src/styles.ts
const fontStyle = memoize(({ fontFamily, size, theme = lightTheme }) => {
	return style(theme.fontStyles[size], { fontFamily: theme.fontFamilies[fontFamily] });
});
const borderStyle = memoize(({ width, topWidth, leftWidth, rightWidth, bottomWidth, color = "sand6", theme = lightTheme }) => {
	const hasBorder = width || topWidth || leftWidth || rightWidth || bottomWidth;
	return style({
		...hasBorder && width ? { borderWidth: width ? theme.borderWidths[width] : 0 } : { borderWidth: 0 },
		...hasBorder && topWidth ? { borderTopWidth: topWidth ? theme.borderWidths[topWidth] : 0 } : {},
		...hasBorder && leftWidth ? { borderLeftWidth: leftWidth ? theme.borderWidths[leftWidth] : 0 } : {},
		...hasBorder && rightWidth ? { borderRightWidth: rightWidth ? theme.borderWidths[rightWidth] : 0 } : {},
		...hasBorder && bottomWidth ? { borderBottomWidth: bottomWidth ? theme.borderWidths[bottomWidth] : 0 } : {},
		borderStyle: hasBorder ? "solid" : void 0,
		borderColor: theme.colors[color]
	});
});
const flexboxStyle = memoize(({ flex, basis, grow, shrink, alignSelf, column, gap, align, justify, wrap, inline, theme = lightTheme }) => {
	return style({
		display: inline ? "inline-flex" : "flex",
		flex: flex === true ? "1 1 auto" : flex,
		flexDirection: column ? "column" : "row",
		gap: gap ? theme.spacing[gap] : void 0,
		alignItems: align,
		justifyContent: justify,
		flexWrap: wrap === true ? "wrap" : wrap,
		flexBasis: basis,
		flexGrow: grow === true ? 1 : grow,
		flexShrink: shrink === true ? 1 : shrink,
		alignSelf
	});
});
const paddingStyle = memoize(({ all, vertical, horizontal, top, bottom, left, right, theme = lightTheme }) => {
	return style({
		padding: all ? theme.spacing[all] : void 0,
		paddingTop: top ? theme.spacing[top] : vertical ? theme.spacing[vertical] : void 0,
		paddingBottom: bottom ? theme.spacing[bottom] : vertical ? theme.spacing[vertical] : void 0,
		paddingLeft: left ? theme.spacing[left] : horizontal ? theme.spacing[horizontal] : void 0,
		paddingRight: right ? theme.spacing[right] : horizontal ? theme.spacing[horizontal] : void 0
	});
});
const uiElementPaddingStyle = memoize(({ size }) => {
	return paddingStyle({
		horizontal: match(size, {
			xs: "xs",
			sm: "sm",
			md: "md"
		}),
		vertical: match(size, {
			xs: "xxs",
			sm: "xs",
			md: "sm"
		})
	});
});
const transitionStyle = memoize(({ transition, theme = lightTheme }) => {
	const { duration, easing } = theme.transitions[transition];
	return style({
		transition: `opacity ${duration}ms ${easing}, transform ${duration}ms ${easing}`,
		"@media (prefers-reduced-motion: reduce)": { transition: "none" }
	});
});
/**
* Focus ring style using accent color at ~30% opacity.
* Uses box-shadow for a 2px ring that adapts to light/dark mode.
*
* @param includeFocused - If true, also applies focus ring on `data-focused` (for inputs).
*/
const focusRingStyle = memoize(({ theme = lightTheme, includeFocused = false }) => {
	const focusStyles = { boxShadow: `0 0 0 2px ${theme.colors.accentA6}` };
	return style({
		outline: "none",
		"&[data-focus-visible]": focusStyles,
		...includeFocused && { "&[data-focused]": focusStyles }
	});
});

//#endregion
export { borderStyle, flexboxStyle, focusRingStyle, fontStyle, paddingStyle, transitionStyle, uiElementPaddingStyle };